/**
 * MarkdownText — minimal in-house markdown renderer for AI study assets
 * (summary, recruiter script) and the in-app CRM handbook. Supports #/##/###
 * headings, **bold**, unordered (-/*) and ordered (1.) lists, and paragraphs.
 * Also renders in-page `[text](#anchor)` links and emits heading `id` anchors
 * so the handbook gets a clickable table of contents. Deliberately tiny: still
 * no raw HTML passthrough, no external links, no dependency — the content is
 * our own AI output / repo-versioned docs that we reviewed, not arbitrary user
 * markdown. External (`http(s)`/arbitrary) links are NOT linkified and render
 * as literal text, preserving the no-injection posture for the shared AI-asset
 * render path.
 */
import { ReactNode } from 'react';

/**
 * GitHub-style heading slug: lowercase, strip punctuation (keep word chars,
 * spaces, hyphens), trim, then spaces -> hyphens. Mirrors what authors target
 * with `[Daily workflow](#daily-workflow)`.
 */
const slug = (s: string) =>
  s.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');

// One combined tokenizer for both inline markers, interleaving plain text,
// bold, and in-page anchor links in a single left-to-right pass. A link is
// `[label](#anchor)`; a bold run is `**text**`. Lines with only **bold**
// (no links) produce byte-identical output to the prior bold-only split.
const INLINE_RE = /\[([^\]]+)\]\(#([\w-]+)\)|\*\*(.+?)\*\*/g;

function inline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const start = m.index ?? 0;
    if (start > last) {
      nodes.push(<span key={`${keyBase}-t${i}`}>{text.slice(last, start)}</span>);
    }
    if (m[1] !== undefined) {
      // [label](#anchor) — in-page link only.
      nodes.push(
        <a
          key={`${keyBase}-a${i}`}
          href={`#${m[2]}`}
          className="text-primary-700 underline hover:text-primary-800 dark:text-primary-400"
        >
          {m[1]}
        </a>
      );
    } else {
      // **bold**
      nodes.push(
        <strong key={`${keyBase}-b${i}`} className="font-semibold text-gray-900 dark:text-gray-100">{m[3]}</strong>
      );
    }
    last = start + m[0].length;
    i += 1;
  }
  if (last < text.length) {
    nodes.push(<span key={`${keyBase}-t${i}`}>{text.slice(last)}</span>);
  }
  return nodes;
}

export default function MarkdownText({ text, className = '' }: { text: string; className?: string }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];

  const flushList = (key: string) => {
    if (!list) return;
    const items = list.items.map((item, i) => (
      <li key={`${key}-li${i}`} className="text-sm text-gray-700 dark:text-gray-300">{inline(item, `${key}-li${i}`)}</li>
    ));
    blocks.push(
      list.ordered ? (
        <ol key={key} className="ml-5 list-decimal space-y-1">{items}</ol>
      ) : (
        <ul key={key} className="ml-5 list-disc space-y-1">{items}</ul>
      )
    );
    list = null;
  };
  const flushPara = (key: string) => {
    if (para.length === 0) return;
    blocks.push(
      <p key={key} className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">{inline(para.join(' '), key)}</p>
    );
    para = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const key = `md${idx}`;
    const heading = /^(#{1,3})\s+(.*)$/.exec(line.trim());
    const bullet = /^[-*]\s+(.*)$/.exec(line.trim());
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line.trim());

    if (!line.trim()) {
      flushPara(`${key}-p`);
      flushList(`${key}-l`);
      return;
    }
    if (heading) {
      flushPara(`${key}-p`);
      flushList(`${key}-l`);
      const level = heading[1].length;
      const cls =
        level === 1
          ? 'text-base font-semibold text-gray-900 dark:text-gray-100'
          : level === 2
            ? 'text-sm font-semibold text-gray-900 dark:text-gray-100'
            : 'text-sm font-medium text-gray-800 dark:text-gray-200';
      // Slug from the RAW heading text (before inline) so **bold** markers in a
      // heading do not pollute the id. scroll-mt-20 keeps an in-page jump from
      // tucking the heading under the sticky CRM header.
      blocks.push(<div key={key} id={slug(heading[2])} className={`${cls} mt-1 scroll-mt-20`}>{inline(heading[2], key)}</div>);
      return;
    }
    if (bullet || numbered) {
      flushPara(`${key}-p`);
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flushList(`${key}-l`);
        list = { ordered, items: [] };
      }
      list.items.push((bullet || numbered)![1]);
      return;
    }
    flushList(`${key}-l`);
    para.push(line.trim());
  });
  flushPara('md-final-p');
  flushList('md-final-l');

  return <div className={`space-y-2 ${className}`}>{blocks}</div>;
}
